import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import { usePersonnel } from "../../hooks/usePersonnel";
import { getAssignmentUrl } from "../../config/urls";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useVirtualScroll } from "../../hooks/useVirtualScroll";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ErrorMessage from "../../components/common/ErrorMessage";
import {
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaPowerOff,
  FaCheckCircle,
  FaSearch,
  FaExternalLinkAlt,
} from "react-icons/fa";
import "./EmployeePagesManagement.css";

const PAGE_STATUS_FILTER_ALL = "all";
const PAGE_STATUS_FILTER_ACTIVE = "active";
const PAGE_STATUS_FILTER_INACTIVE = "inactive";

const EMPLOYEE_PAGES_VIRTUAL_THRESHOLD = 28;
const EMPLOYEE_MOBILE_CARD_GAP = 14;
const EMPLOYEE_DESKTOP_ROW_GAP = 8;
const DEFAULT_MOBILE_CARD_HEIGHT = 232;
const DEFAULT_DESKTOP_ROW_HEIGHT = 100;
const LONG_PRESS_MS = 550;
const COPY_TOAST_MS = 2500;

async function copyTextToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function truncateMiddle(str, maxLen) {
  if (!str || str.length <= maxLen) return str;
  const keep = Math.max(8, Math.floor((maxLen - 3) / 2));
  return `${str.slice(0, keep)}…${str.slice(-keep)}`;
}

const CopyableAssignmentUrl = React.memo(function CopyableAssignmentUrl({
  url,
  compact,
  onCopied,
}) {
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  const runCopy = useCallback(
    async (e) => {
      e?.preventDefault?.();
      const ok = await copyTextToClipboard(url);
      if (ok) onCopied?.();
    },
    [url, onCopied]
  );

  const startLongPress = useCallback(() => {
    longPressFired.current = false;
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = window.setTimeout(() => {
      longPressFired.current = true;
      runCopy();
    }, LONG_PRESS_MS);
  }, [runCopy]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleLinkClick = useCallback(
    (e) => {
      if (longPressFired.current) {
        e.preventDefault();
        longPressFired.current = false;
      }
    },
    []
  );

  useEffect(() => {
    return () => cancelLongPress();
  }, [cancelLongPress]);

  const display = compact ? truncateMiddle(url, 48) : url;

  return (
    <span
      className={`copyable-url-wrap${compact ? " copyable-url-wrap--compact" : ""}`}
      onTouchStart={startLongPress}
      onTouchEnd={cancelLongPress}
      onTouchCancel={cancelLongPress}
      onTouchMove={cancelLongPress}
      onContextMenu={(e) => {
        e.preventDefault();
        runCopy(e);
      }}
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="page-url"
        onClick={handleLinkClick}
        title="פתיחה בלשונית חדשה · לחיצה ארוכה או תפריט הקשר — העתקה"
      >
        {display}
        <FaExternalLinkAlt className="page-url-open-icon" aria-hidden />
      </a>
      <span className="copyable-url-hint">לחיצה ארוכה להעתקה</span>
    </span>
  );
});

const EmployeePageMobileCard = React.memo(function EmployeePageMobileCard({
  page,
  personnelName,
  assignmentUrl,
  isActive,
  createdLabel,
  transactions,
  operationLoading,
  onToggle,
  onDelete,
  onUrlCopied,
}) {
  return (
    <div
      className={`employee-mobile-card${isActive ? "" : " employee-mobile-card-inactive"}`}
    >
      <div className="employee-mobile-card-header">
        <span className="employee-mobile-name">{personnelName}</span>
        <span
          className={`status-badge ${isActive ? "active" : "inactive"}`}
        >
          {isActive ? "פעיל" : "מושבת"}
        </span>
      </div>
      <div className="employee-mobile-body">
        <div className="employee-mobile-field">
          <span className="employee-mobile-label">מספר אישי</span>
          <span className="employee-mobile-value">
            {page.personnelId || "—"}
          </span>
        </div>
        <div className="employee-mobile-field">
          <span className="employee-mobile-label">מזהה דף</span>
          <code className="page-id employee-mobile-page-id">{page.id}</code>
        </div>
        <div className="employee-mobile-field employee-mobile-field-url">
          <span className="employee-mobile-label">קישור</span>
          <CopyableAssignmentUrl
            url={assignmentUrl}
            compact={false}
            onCopied={onUrlCopied}
          />
        </div>
        <div className="employee-mobile-field">
          <span className="employee-mobile-label">נוצר</span>
          <span className="employee-mobile-value">{createdLabel}</span>
        </div>
        <div className="employee-mobile-field">
          <span className="employee-mobile-label">עסקאות</span>
          <span className="employee-mobile-value">{transactions}</span>
        </div>
      </div>
      <div className="employee-mobile-actions">
        <button
          type="button"
          className={`btn-toggle ${isActive ? "active" : "inactive"}`}
          onClick={() => onToggle(page.id, isActive)}
          disabled={operationLoading}
          title={isActive ? "השבת דף" : "הפעל דף"}
        >
          {isActive ? <FaToggleOn /> : <FaToggleOff />}
          <span>{isActive ? "השבת" : "הפעל"}</span>
        </button>
        <button
          type="button"
          className="btn-delete"
          onClick={() => onDelete(page.id, personnelName)}
          disabled={operationLoading}
          title="מחק דף"
        >
          <FaTrash />
          <span>מחק</span>
        </button>
      </div>
    </div>
  );
});

const EmployeePageDesktopVirtualRow = React.memo(
  function EmployeePageDesktopVirtualRow({
    page,
    personnelName,
    assignmentUrl,
    isActive,
    createdLabel,
    transactions,
    operationLoading,
    onToggle,
    onDelete,
    onUrlCopied,
  }) {
    return (
      <div
        className={`employee-desktop-virtual-row${isActive ? "" : " employee-desktop-virtual-row-inactive"}`}
      >
        <div className="employee-desktop-cell employee-desktop-name">
          {personnelName}
        </div>
        <div className="employee-desktop-cell">{page.personnelId || "—"}</div>
        <div className="employee-desktop-cell">
          <code className="page-id">{page.id}</code>
        </div>
        <div className="employee-desktop-cell employee-desktop-url">
          <CopyableAssignmentUrl
            url={assignmentUrl}
            compact
            onCopied={onUrlCopied}
          />
        </div>
        <div className="employee-desktop-cell">
          <span className={`status-badge ${isActive ? "active" : "inactive"}`}>
            {isActive ? "פעיל" : "מושבת"}
          </span>
        </div>
        <div className="employee-desktop-cell employee-desktop-date">
          {createdLabel}
        </div>
        <div className="employee-desktop-cell employee-desktop-num">
          {transactions}
        </div>
        <div className="employee-desktop-cell employee-desktop-actions">
          <button
            type="button"
            className={`btn-toggle ${isActive ? "active" : "inactive"}`}
            onClick={() => onToggle(page.id, isActive)}
            disabled={operationLoading}
            title={isActive ? "השבת דף" : "הפעל דף"}
          >
            {isActive ? <FaToggleOn /> : <FaToggleOff />}
          </button>
          <button
            type="button"
            className="btn-delete"
            onClick={() => onDelete(page.id, personnelName)}
            disabled={operationLoading}
            title="מחק דף"
          >
            <FaTrash />
          </button>
        </div>
      </div>
    );
  }
);

const EmployeePagesManagement = () => {
  const isMobile = useIsMobile();
  const { personnel } = usePersonnel();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [operationLoading, setOperationLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageStatusFilter, setPageStatusFilter] = useState(PAGE_STATUS_FILTER_ALL);
  const [copyToast, setCopyToast] = useState("");

  const clearCopyToastTimer = useRef(null);
  const showCopiedToast = useCallback(() => {
    if (clearCopyToastTimer.current) {
      clearTimeout(clearCopyToastTimer.current);
    }
    setCopyToast("הקישור הועתק ללוח");
    clearCopyToastTimer.current = setTimeout(() => {
      setCopyToast("");
      clearCopyToastTimer.current = null;
    }, COPY_TOAST_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (clearCopyToastTimer.current) {
        clearTimeout(clearCopyToastTimer.current);
      }
    };
  }, []);

  const personnelMap = useMemo(() => {
    const map = new Map();
    if (personnel && personnel.length > 0) {
      personnel.forEach((p) => {
        map.set(p.personalNumber, p);
      });
    }
    return map;
  }, [personnel]);

  useEffect(() => {
    const loadPages = async () => {
      try {
        setLoading(true);
        setError("");
        const pagesRef = collection(db, "employeePages");
        const snapshot = await getDocs(pagesRef);
        const pagesData = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setPages(pagesData);
      } catch (err) {
        console.error("Error loading employee pages:", err);
        setError("שגיאה בטעינת דפי החתימה");
      } finally {
        setLoading(false);
      }
    };

    loadPages();
  }, []);

  const allPagesActive = useMemo(() => {
    if (pages.length === 0) return false;
    return pages.every((page) => page.isActive !== false);
  }, [pages]);

  const togglePageActive = useCallback(async (pageId, currentStatus) => {
    try {
      setOperationLoading(true);
      const pageRef = doc(db, "employeePages", pageId);
      await updateDoc(pageRef, {
        isActive: !currentStatus,
        lastUpdated: new Date(),
      });
      setPages((prev) =>
        prev.map((page) =>
          page.id === pageId ? { ...page, isActive: !currentStatus } : page
        )
      );
    } catch (err) {
      console.error("Error toggling page status:", err);
      alert("שגיאה בעדכון סטטוס הדף");
    } finally {
      setOperationLoading(false);
    }
  }, []);

  const toggleAllPages = useCallback(async () => {
    try {
      setOperationLoading(true);
      const batch = writeBatch(db);
      const newStatus = !allPagesActive;

      pages.forEach((page) => {
        const pageRef = doc(db, "employeePages", page.id);
        batch.update(pageRef, {
          isActive: newStatus,
          lastUpdated: new Date(),
        });
      });

      await batch.commit();
      setPages((prev) =>
        prev.map((page) => ({ ...page, isActive: newStatus }))
      );
    } catch (err) {
      console.error("Error toggling all pages:", err);
      alert("שגיאה בעדכון כל הדפים");
    } finally {
      setOperationLoading(false);
    }
  }, [pages, allPagesActive]);

  const deletePage = useCallback(async (pageId, personnelName) => {
    if (
      !window.confirm(`האם אתה בטוח שברצונך למחוק את הדף של ${personnelName}?`)
    ) {
      return;
    }

    try {
      setOperationLoading(true);
      const pageRef = doc(db, "employeePages", pageId);
      await deleteDoc(pageRef);
      setPages((prev) => prev.filter((page) => page.id !== pageId));
    } catch (err) {
      console.error("Error deleting page:", err);
      alert("שגיאה במחיקת הדף");
    } finally {
      setOperationLoading(false);
    }
  }, []);

  const pagesMatchingStatus = useMemo(() => {
    if (pageStatusFilter === PAGE_STATUS_FILTER_ACTIVE) {
      return pages.filter((p) => p.isActive !== false);
    }
    if (pageStatusFilter === PAGE_STATUS_FILTER_INACTIVE) {
      return pages.filter((p) => p.isActive === false);
    }
    return pages;
  }, [pages, pageStatusFilter]);

  const filteredPages = useMemo(() => {
    if (!searchTerm.trim()) return pagesMatchingStatus;

    const term = searchTerm.trim().toLowerCase();
    return pagesMatchingStatus.filter((page) => {
      const personnelData = personnelMap.get(page.personnelId);
      const personnelName = personnelData
        ? `${personnelData.firstName} ${personnelData.lastName}`
        : "";
      const personalNumber = page.personnelId || "";
      const pageId = page.id || "";

      return (
        personnelName.toLowerCase().includes(term) ||
        personalNumber.toLowerCase().includes(term) ||
        pageId.toLowerCase().includes(term)
      );
    });
  }, [pagesMatchingStatus, searchTerm, personnelMap]);

  const pagesSummary = useMemo(() => {
    const activeCount = pages.filter((p) => p.isActive !== false).length;
    const inactiveCount = pages.filter((p) => p.isActive === false).length;
    return {
      total: pages.length,
      activeCount,
      inactiveCount,
    };
  }, [pages]);

  const emptyListMessage = useMemo(() => {
    if (!pages.length) return "אין דפי חתימה במערכת";
    if (!pagesMatchingStatus.length) {
      if (pageStatusFilter === PAGE_STATUS_FILTER_ACTIVE) {
        return "אין דפים פעילים";
      }
      if (pageStatusFilter === PAGE_STATUS_FILTER_INACTIVE) {
        return "אין דפים מושבתים";
      }
      return "אין דפי חתימה במערכת";
    }
    if (!filteredPages.length) {
      return "לא נמצאו דפים התואמים לחיפוש";
    }
    return "";
  }, [
    pages.length,
    pagesMatchingStatus.length,
    filteredPages.length,
    pageStatusFilter,
  ]);

  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return "לא זמין";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString("he-IL", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (err) {
      return "לא זמין";
    }
  }, []);

  const shouldVirtualize = useMemo(
    () => filteredPages.length >= EMPLOYEE_PAGES_VIRTUAL_THRESHOLD,
    [filteredPages.length]
  );

  const [mobileCardHeight, setMobileCardHeight] = useState(
    DEFAULT_MOBILE_CARD_HEIGHT
  );
  const [desktopRowHeight, setDesktopRowHeight] = useState(
    DEFAULT_DESKTOP_ROW_HEIGHT
  );

  const mobileContainerHeight = useMemo(() => {
    if (!isMobile || typeof window === "undefined") return 500;
    const reserved = 300;
    const available = window.innerHeight - reserved;
    return Math.max(280, Math.min(available, 880));
  }, [isMobile]);

  const desktopContainerHeight = useMemo(() => {
    if (isMobile || typeof window === "undefined") return 520;
    const reserved = 260;
    const available = window.innerHeight - reserved;
    return Math.max(380, Math.min(available, 900));
  }, [isMobile]);

  const itemHeight = isMobile ? mobileCardHeight : desktopRowHeight;
  const gap = isMobile ? EMPLOYEE_MOBILE_CARD_GAP : EMPLOYEE_DESKTOP_ROW_GAP;
  const effectiveItemHeight = itemHeight + gap;

  const virtualItems = useMemo(
    () => (shouldVirtualize ? filteredPages : []),
    [shouldVirtualize, filteredPages]
  );

  const containerHeight = isMobile ? mobileContainerHeight : desktopContainerHeight;

  const {
    visibleItems: virtualVisiblePages,
    totalHeight: virtualTotalHeight,
    offsetY: virtualOffsetY,
    setContainerRef: setVirtualContainerRef,
  } = useVirtualScroll(virtualItems, effectiveItemHeight, containerHeight);

  const virtualContainerRef = useRef(null);
  const measureSlotRef = useRef(null);

  const setVirtualContainerCallback = useCallback(
    (node) => {
      virtualContainerRef.current = node;
      if (node && shouldVirtualize) {
        setVirtualContainerRef(node);
      } else {
        setVirtualContainerRef(null);
      }
    },
    [shouldVirtualize, setVirtualContainerRef]
  );

  useEffect(() => {
    if (shouldVirtualize) {
      setMobileCardHeight(DEFAULT_MOBILE_CARD_HEIGHT);
      setDesktopRowHeight(DEFAULT_DESKTOP_ROW_HEIGHT);
    }
  }, [shouldVirtualize]);

  useEffect(() => {
    if (!shouldVirtualize || !measureSlotRef.current) return;

    const measure = () => {
      const wrap = measureSlotRef.current;
      if (wrap?.firstElementChild) {
        const inner = wrap.firstElementChild;
        const measured = inner.offsetHeight || wrap.offsetHeight;
        if (measured > 0) {
          if (isMobile) {
            setMobileCardHeight(measured + EMPLOYEE_MOBILE_CARD_GAP);
          } else {
            setDesktopRowHeight(measured + EMPLOYEE_DESKTOP_ROW_GAP);
          }
        }
      }
    };

    const t = window.setTimeout(measure, 120);
    const onResize = () => window.setTimeout(measure, 80);
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, [
    shouldVirtualize,
    isMobile,
    virtualVisiblePages.length,
    filteredPages.length,
  ]);

  const renderPageRowProps = useCallback(
    (page) => {
      const personnelData = personnelMap.get(page.personnelId);
      const personnelName = personnelData
        ? `${personnelData.firstName} ${personnelData.lastName}`
        : page.personnelId || "לא נמצא";
      const isActive = page.isActive !== false;
      return {
        page,
        personnelName,
        assignmentUrl: getAssignmentUrl(page.id),
        isActive,
        createdLabel: formatTimestamp(page.createdAt),
        transactions: page.totalTransactionsCount || 0,
        operationLoading,
        onToggle: togglePageActive,
        onDelete: deletePage,
        onUrlCopied: showCopiedToast,
      };
    },
    [
      personnelMap,
      formatTimestamp,
      operationLoading,
      togglePageActive,
      deletePage,
      showCopiedToast,
    ]
  );

  const scopeProps = {
    id: "employee-pages-scope",
    className: "employee-pages-management",
  };

  if (loading) {
    return (
      <div {...scopeProps}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div {...scopeProps}>
        <ErrorMessage message={error} />
      </div>
    );
  }

  const virtualListInner = shouldVirtualize && (
    <>
      {!isMobile && (
        <div className="employee-desktop-virtual-header" role="row">
          <div className="employee-desktop-cell">שם עובד</div>
          <div className="employee-desktop-cell">מספר אישי</div>
          <div className="employee-desktop-cell">מזהה דף</div>
          <div className="employee-desktop-cell">URL</div>
          <div className="employee-desktop-cell">סטטוס</div>
          <div className="employee-desktop-cell">תאריך יצירה</div>
          <div className="employee-desktop-cell">עסקאות</div>
          <div className="employee-desktop-cell">פעולות</div>
        </div>
      )}
      <div
        ref={setVirtualContainerCallback}
        className={
          isMobile
            ? "employee-mobile-scroll"
            : "employee-desktop-virtual-scroll"
        }
        style={{
          height: containerHeight,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <div
          style={{
            height: virtualTotalHeight,
            position: "relative",
          }}
        >
          <div
            className={
              isMobile
                ? "employee-mobile-cards-inner"
                : "employee-desktop-virtual-inner"
            }
            style={{
              transform: `translateY(${virtualOffsetY}px)`,
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
            }}
          >
            {virtualVisiblePages.map((page, index) => {
              const props = renderPageRowProps(page);
              const key = page.id;
              return (
                <div
                  key={key}
                  ref={
                    index === 0 && virtualVisiblePages.length > 0
                      ? measureSlotRef
                      : null
                  }
                  className={
                    isMobile
                      ? "employee-mobile-card-slot"
                      : "employee-desktop-row-slot"
                  }
                  style={{
                    height: effectiveItemHeight,
                    boxSizing: "border-box",
                  }}
                >
                  {isMobile ? (
                    <EmployeePageMobileCard {...props} />
                  ) : (
                    <EmployeePageDesktopVirtualRow {...props} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <p
        className="employee-pages-virtual-count"
        aria-live="polite"
      >{`סה"כ ${filteredPages.length} דפים · גלילה וירטואלית`}</p>
    </>
  );

  return (
    <div {...scopeProps}>
      <div
        className={`copy-toast${copyToast ? " copy-toast-visible" : ""}`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {copyToast}
      </div>

      <div className="page-header">
        <h1>ניהול דפי חתימה</h1>
        <div className="header-actions">
          <button
            type="button"
            className={`btn-toggle-all ${
              allPagesActive ? "active" : "inactive"
            }`}
            onClick={toggleAllPages}
            disabled={operationLoading || pages.length === 0}
          >
            {allPagesActive ? (
              <>
                <FaPowerOff /> השבת את כל הדפים
              </>
            ) : (
              <>
                <FaCheckCircle /> הפעל את כל הדפים
              </>
            )}
          </button>
        </div>
      </div>

      <div className="search-container">
        <div className="search-box">
          <FaSearch className="search-icon" aria-hidden />
          <input
            type="search"
            placeholder="חפש לפי שם עובד, מספר אישי או מזהה דף..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            enterKeyHint="search"
          />
        </div>
      </div>

      <div
        className="employee-pages-statistics"
        role="toolbar"
        aria-label="סינון לפי סטטוס דף"
      >
        <button
          type="button"
          className={`employee-stat-card-filter${pageStatusFilter === PAGE_STATUS_FILTER_ALL ? " employee-stat-card-filter-active" : ""}`}
          onClick={() => setPageStatusFilter(PAGE_STATUS_FILTER_ALL)}
          aria-pressed={pageStatusFilter === PAGE_STATUS_FILTER_ALL}
        >
          <span className="employee-stat-label">סה"כ דפים</span>
          <span className="employee-stat-value">{pagesSummary.total}</span>
        </button>
        <button
          type="button"
          className={`employee-stat-card-filter${pageStatusFilter === PAGE_STATUS_FILTER_ACTIVE ? " employee-stat-card-filter-active" : ""}`}
          onClick={() => setPageStatusFilter(PAGE_STATUS_FILTER_ACTIVE)}
          aria-pressed={pageStatusFilter === PAGE_STATUS_FILTER_ACTIVE}
        >
          <span className="employee-stat-label">פעילים</span>
          <span className="employee-stat-value">
            {pagesSummary.activeCount}
          </span>
        </button>
        <button
          type="button"
          className={`employee-stat-card-filter${pageStatusFilter === PAGE_STATUS_FILTER_INACTIVE ? " employee-stat-card-filter-active" : ""}`}
          onClick={() => setPageStatusFilter(PAGE_STATUS_FILTER_INACTIVE)}
          aria-pressed={pageStatusFilter === PAGE_STATUS_FILTER_INACTIVE}
        >
          <span className="employee-stat-label">מושבתים</span>
          <span className="employee-stat-value">
            {pagesSummary.inactiveCount}
          </span>
        </button>
      </div>
      {shouldVirtualize && (
        <p className="employee-pages-virtual-hint-banner">
          תצוגה מהירה — גלילה וירטואלית
        </p>
      )}

      {filteredPages.length === 0 ? (
        <div className="no-pages">
          <p>{emptyListMessage}</p>
        </div>
      ) : shouldVirtualize ? (
        virtualListInner
      ) : isMobile ? (
        <div className="employee-mobile-cards">
          {filteredPages.map((page) => (
            <EmployeePageMobileCard
              key={page.id}
              {...renderPageRowProps(page)}
            />
          ))}
          <p className="employee-pages-virtual-count" aria-live="polite">
            {`סה"כ ${filteredPages.length} דפים`}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="pages-table">
            <thead>
              <tr>
                <th>שם עובד</th>
                <th>מספר אישי</th>
                <th>מזהה דף</th>
                <th>URL</th>
                <th>סטטוס</th>
                <th>תאריך יצירה</th>
                <th>עסקאות</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {filteredPages.map((page) => {
                const personnelData = personnelMap.get(page.personnelId);
                const personnelName = personnelData
                  ? `${personnelData.firstName} ${personnelData.lastName}`
                  : page.personnelId || "לא נמצא";
                const isActive = page.isActive !== false;
                const assignmentUrl = getAssignmentUrl(page.id);

                return (
                  <tr
                    key={page.id}
                    className={isActive ? "active" : "inactive"}
                  >
                    <td>{personnelName}</td>
                    <td>{page.personnelId || "-"}</td>
                    <td>
                      <code className="page-id">{page.id}</code>
                    </td>
                    <td className="pages-table-url-cell">
                      <CopyableAssignmentUrl
                        url={assignmentUrl}
                        compact={false}
                        onCopied={showCopiedToast}
                      />
                    </td>
                    <td>
                      <span
                        className={`status-badge ${
                          isActive ? "active" : "inactive"
                        }`}
                      >
                        {isActive ? "פעיל" : "מושבת"}
                      </span>
                    </td>
                    <td>{formatTimestamp(page.createdAt)}</td>
                    <td>{page.totalTransactionsCount || 0}</td>
                    <td>
                      <div className="actions">
                        <button
                          type="button"
                          className={`btn-toggle ${
                            isActive ? "active" : "inactive"
                          }`}
                          onClick={() => togglePageActive(page.id, isActive)}
                          disabled={operationLoading}
                          title={isActive ? "השבת דף" : "הפעל דף"}
                        >
                          {isActive ? <FaToggleOn /> : <FaToggleOff />}
                        </button>
                        <button
                          type="button"
                          className="btn-delete"
                          onClick={() => deletePage(page.id, personnelName)}
                          disabled={operationLoading}
                          title="מחק דף"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmployeePagesManagement;
